import React, { useEffect, useState, useCallback } from 'react';
import Icon from 'react-native-vector-icons/dist/Feather';
import { useTheme } from 'styled-components/native';
import { TouchableOpacity, View } from 'react-native';
import { useRoute } from '@react-navigation/native';
import { useApi } from '~/hooks/api';
import { serializeString } from '~/utils/strings';
import { usePageWithCardSettings, useStepper } from '~/components/@tem-ui/Stepper';
import { useAppointment } from '~/hooks/appointment';
import { useTracking } from '~/services/tracking';
import { trackingEventType } from '~/contexts/tracking/types';
import { ProfessionalType, Specialty } from '~/contexts/types';
import { Input } from '~/components/@hello-ui/Input';
import { Typography } from '~/components/@hello-ui';
import { Listing } from '~/components/@hello-ui/Listing';

export const ScheduleTelemedicineSpecialty = (): JSX.Element => {
  usePageWithCardSettings({ scrollEnabled: false });
  const api = useApi();
  const theme = useTheme();

  const { appointment, setAppointment } = useAppointment();
  const { nextStep } = useStepper();
  const { tracking } = useTracking();
  const { benefit } = useRoute().params;

  const [term, setTerm] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [fullSpecialties, setFullSpecialtie] = useState<Specialty[]>([]);

  useEffect(() => {
    setAppointment({
      ...appointment,
      specialty: null,
    });
  }, []);

  const sendMovementRecordAppointmentStatus = useCallback((specialty: Specialty) => {
    const professionalType = benefit === 'programa_nutricional'  || benefit === 'conexa_habitos_saudaveis' ? specialty.id : null;

    void tracking.addMovement({
      event: trackingEventType.ClickedOnScheduleSpecialty,
      metadata: {
        appointment: {
          specialty,
        },
      },
    });

    void setAppointment({
      ...appointment,
      specialty,
      professionalType,
    });
  }, []);

  const renderItem = useCallback(({ item }) => {
    return (
      <TouchableOpacity
        className="w-full flex-row py-24"
        onPress={() => {
          sendMovementRecordAppointmentStatus(item);
          nextStep();
        }}>
        <Typography variant="body2" color={theme.colors.black}>
          {item.name}
        </Typography>
        <View className="absolute right-0 top-24">
          <Icon name="chevron-right" size={24} color={theme?.colors?.primary} />
        </View>
      </TouchableOpacity>
    );
  }, []);

  const getData = useCallback(async () => {
    const { data: specialties } = await api.telemedicineSpecialties();
    const { data: professionalTypes } = await api.professionalTypes();

    if (benefit === 'programa_nutricional') {
      let professionalTypesFiltered = professionalTypes.data.filter((professionalType: ProfessionalType) => professionalType === 'NUTRITIONIST' || professionalType === 'PHYSICAL_EDUCATOR');
      professionalTypesFiltered = professionalTypesFiltered.map((professionalType: ProfessionalType) => ({
        id: professionalType,
        name: professionalType === 'NUTRITIONIST' ? 'Nutrição Clínica' : 'Educação Física',
      }));
      setSpecialties(professionalTypesFiltered);
    } else if (benefit === 'conexa_habitos_saudaveis') {
      let professionalTypesFiltered = professionalTypes.data.filter((professionalType: ProfessionalType) => professionalType === "PSYCHOLOGIST" || professionalType === 'PHYSICAL_EDUCATOR' || professionalType === "NUTRITIONIST");
      professionalTypesFiltered = professionalTypesFiltered.map((professionalType: ProfessionalType) => ({
        id: professionalType,
        name: professionalType === 'NUTRITIONIST' ? 'Nutrição Clínica' : professionalType === 'PSYCHOLOGIST' ? 'Psicologia' : 'Educação Física',
      }));
      setSpecialties(professionalTypesFiltered);
    }
    else {
      setSpecialties(specialties.data);
    }
    setFullSpecialtie(specialties.data);

    setLoading(false);
  }, []);

  const handleSearch = useCallback(
    (text: string) => {
      const query: string = serializeString(text);
      const filteredData = fullSpecialties.filter(({ name }: Specialty) =>
        serializeString(name).includes(query),
      );

      setSpecialties(filteredData);
      setTerm(text);
    },
    [fullSpecialties],
  );

  useEffect(() => {
    void getData();
  }, []);

  return (
    <>
      <View className="w-full max-w-[576px]">
        <Typography variant="title">Qual especialidade?</Typography>
      </View>
      <View className="mt-24 mobile:mt-16">
        <Input
          variant="search"
          value={term}
          onChangeText={(value) => handleSearch(value)}
          label="Pesquisar"
          placeholder="Digite a especialidade"
        />
      </View>
      <Listing data={specialties} loading={loading} renderItem={renderItem} useSeparator={true} />
    </>
  );
};
